import { useAuthoringEffort, type AuthoringEffort } from '@/features/coaching-effort/useAuthoringEffort';
import { AuthoringEffortControls } from '@/features/coaching-effort/AuthoringEffortControls';
import type { EditCategory } from '@/features/coaching-effort/EffortSession';
import {
  confirmationFactSchema,
  humanConfirmationFact,
  type ConfirmationForm,
} from "./confirmationFact";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { useAuthStore } from "@/stores/auth";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  Composer,
  ComposerPlayerPicker,
  type ComposerHandle,
} from "@/features/coach-review/components/Composer";
import { GoalWritingFields } from "@/features/coach/components/GoalWritingFields";
import { useRoster } from "@/features/coach/hooks/useRoster";
import {
  draftContextKey,
  isCurrentDraftContext,
  useComposerDraftContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
import { ApiError, errorMessage } from "@/lib/api";
import type {
  ApproveConfirmedCoachingDraftRequest,
  CoachingApprovalFields,
  CoachingApplicationDraft,
  CoachingAIJobProgress,
  CoachingConfirmationResponse,
  CoachingPublicationPreview,
  CoachingPublicationSelection,
  CoachingVideoReference,
  CreateCoachingConfirmationRequest,
  DiscardCoachingCaptureRequest,
  GenerateConfirmedCoachingDraftRequest,
} from "@/lib/types";
import { ProposalEvidence } from "./ProposalEvidence";
import { useDraftInbox } from "./useDraftInbox";
import {
  boundedHumanEdits,
  clearReviewCarry,
  clearReviewCarryForOrigin,
  FrozenOperation,
  proposedWriting,
  readReviewCarry,
  recipientsReviewable,
  saveReviewCarry,
} from "./reviewState";

export function DraftInbox({
  onManualFeedback,
}: {
  onManualFeedback: () => void;
}) {
  const owner = useComposerDraftContext();
  return owner ? (
    <OwnedInbox
      key={draftContextKey(owner)}
      owner={owner}
      onManualFeedback={onManualFeedback}
    />
  ) : null;
}
type ApprovalOperation = {
  captureId: string;
  draftId: string;
  body: ApproveConfirmedCoachingDraftRequest;
  metricLabels?: EditCategory[] | null;
};
function approvalOperationKey(request: ApprovalOperation): string {
  return JSON.stringify([
    request.captureId,
    request.draftId,
    request.body.client_operation_id,
  ]);
}
function sameApprovalOperation(
  left: ApprovalOperation | null,
  right: ApprovalOperation,
): boolean {
  return !!left && approvalOperationKey(left) === approvalOperationKey(right);
}
function OwnedInbox({
  owner,
  onManualFeedback,
}: {
  owner: DraftContext;
  onManualFeedback: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [publishedReceipt, setPublishedReceipt] = useState<string | null>(null);
  const [captureId, setCaptureId] = useState<string | null>(
    () => readReviewCarry(owner)?.origin.captureId ?? null,
  );
  const flow = useDraftInbox(owner, captureId);
  const effort = useAuthoringEffort(owner, open && !!captureId);
  const roster = useRoster();
  const pendingApproval = useRef<ApprovalOperation | null>(null);
  const [reconciliationError, setReconciliationError] = useState<{
    operationKey: string;
    message: string;
  } | null>(null);
  const [reconcilingOperationKey, setReconcilingOperationKey] = useState<
    string | null
  >(null);
  const lifetime = useRef(true);
  const itemAvailable = useRef(!!flow.item);
  itemAvailable.current = !!flow.item;
  useEffect(() => {
    lifetime.current = true;
    return () => {
      lifetime.current = false;
    };
  }, []);
  const recordReceipt = (request: ApprovalOperation, result: unknown) => {
    if (!lifetime.current || !isCurrentDraftContext(owner)) return;
    const receipt = result as import("@/lib/types").CoachingApprovalResponse;
    clearReviewCarryForOrigin(owner, request);
    if (sameApprovalOperation(pendingApproval.current, request)) {
      pendingApproval.current = null;
      setReconciliationError((current) =>
        current?.operationKey === approvalOperationKey(request) ? null : current,
      );
    }
    setPublishedReceipt(
      `Published ${receipt.artifact_kind} #${receipt.artifact_id}`,
    );
    setCaptureId((current) => (current === request.captureId ? null : current));
  };
  const observeApproval = (
    request: ApprovalOperation,
    work: () => Promise<unknown>,
  ) => {
    const measurement = effort.session;
    const operation = (async () => {
      await measurement?.beforeSave({ id: request.draftId, revision: request.body.expected_draft_revision }).catch(() => undefined);
      if (!lifetime.current || !isCurrentDraftContext(owner)) throw new Error("Coaching context changed");
      return work();
    })();
    pendingApproval.current = structuredClone(request);
    void operation.then(
      (result) => {
        if (lifetime.current && isCurrentDraftContext(owner)) void measurement?.received({ event: "outcome", outcome: "approved", domain_operation_id: request.body.client_operation_id, edit_categories: request.metricLabels ?? null });
        if (!itemAvailable.current) recordReceipt(request, result);
      },
      (error) => {
        measurement?.domainFailed();
        if (
          lifetime.current &&
          isCurrentDraftContext(owner) &&
          sameApprovalOperation(pendingApproval.current, request)
        )
          setReconciliationError({
            operationKey: approvalOperationKey(request),
            message: errorMessage(error),
          });
      },
    );
    return operation;
  };
  const reconcileApproval = async () => {
    const request = pendingApproval.current;
    if (
      !request ||
      reconcilingOperationKey === approvalOperationKey(request) ||
      !isCurrentDraftContext(owner)
    )
      return;
    const operationKey = approvalOperationKey(request);
    setReconcilingOperationKey(operationKey);
    setReconciliationError((current) =>
      current?.operationKey === operationKey ? null : current,
    );
    try {
      const result = await flow.mutation.mutateAsync(() =>
        observeApproval(request, () => flow.approve(request.draftId, request.body)),
      );
      recordReceipt(request, result);
    } catch (error) {
      if (
        lifetime.current &&
        isCurrentDraftContext(owner) &&
        sameApprovalOperation(pendingApproval.current, request)
      )
        setReconciliationError({ operationKey, message: errorMessage(error) });
    } finally {
      if (lifetime.current && isCurrentDraftContext(owner))
        setReconcilingOperationKey((current) =>
          current === operationKey ? null : current,
        );
    }
  };
  useEffect(() => {
    if (captureId) setOpen(true);
  }, [captureId]);
  const items = flow.inbox.data?.items ?? [];
  return (
    <section
      className="min-w-0 rounded-panel border border-rule bg-surface p-3"
      aria-label="Private draft inbox"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          Private draft inbox ({items.length})
        </Button>
        <span className="text-xs text-ink-muted">
          Only your unfinished captures. Nothing is shared until approval.
        </span>
      </div>
      {publishedReceipt && <p role="status">{publishedReceipt}</p>}
      {effort.session && !flow.item && <AuthoringEffortControls effort={effort} input={() => ({ task_kind: effort.session!.start.task_kind, mode: "assisted", player_ids: [] })} />}
      {open && (
        <div className="mt-3 grid min-w-0 gap-3">
          {flow.inbox.isLoading && (
            <p role="status">Loading private captures…</p>
          )}
          {flow.inbox.error && (
            <p role="alert">
              Inbox unavailable.{" "}
              <Button variant="ghost" onClick={() => void flow.inbox.refetch()}>
                Retry
              </Button>
            </p>
          )}
          {!flow.inbox.isLoading && !flow.inbox.error && !items.length && (
            <p className="text-sm text-ink-muted">No unfinished captures.</p>
          )}
          <div className="grid w-full min-w-0 max-w-[calc(100vw-4rem)] gap-2">
            {items.map((item) => (
              <Button
                key={item.capture_id}
                data-testid={`draft-inbox-capture-${item.capture_id}`}
                variant="outline"
                className="h-auto min-h-10 w-full min-w-0 justify-start whitespace-normal break-words py-2 leading-normal text-left"
                onClick={() => { if (captureId !== item.capture_id) effort.reset(); setCaptureId(item.capture_id); }}
              >
                {item.kind === "marker" ? "Silent marker" : "Saved observation"}{" "}
                · {new Date(item.received_at).toLocaleString()} · {item.status}
              </Button>
            ))}
          </div>
          {captureId && flow.item && (
            <CaptureReview
              key={`${captureId}:${owner.generation}`}
              owner={owner}
              flow={flow}
              players={roster.data?.players ?? []}
              onClose={() => {
                setCaptureId(null);
                clearReviewCarry(owner);
              }}
              observeApproval={observeApproval}
              effort={effort}
              onPublished={(message, request) => {
                if (sameApprovalOperation(pendingApproval.current, request))
                  pendingApproval.current = null;
                setReconciliationError((current) =>
                  current?.operationKey === approvalOperationKey(request)
                    ? null
                    : current,
                );
                setPublishedReceipt(message);
                setCaptureId((current) =>
                  current === request.captureId ? null : current,
                );
              }}
            />
          )}
          {captureId && !flow.item && pendingApproval.current && (
            <div className="grid gap-2" role="status">
              <p>
                Publication acknowledgment is unresolved. Current source and
                proposal are unavailable.
              </p>
              {reconciliationError?.operationKey ===
                approvalOperationKey(pendingApproval.current) && (
                <p role="alert">{reconciliationError.message}</p>
              )}
              <Button
                disabled={
                  reconcilingOperationKey ===
                  approvalOperationKey(pendingApproval.current)
                }
                onClick={() => void reconcileApproval()}
              >
                Retry unchanged operation
              </Button>
            </div>
          )}
          {captureId &&
            !flow.item &&
            !pendingApproval.current &&
            !flow.inbox.isLoading && (
              <p role="status">
                This capture is no longer available in your inbox.{" "}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCaptureId(null);
                    clearReviewCarry(owner);
                  }}
                >
                  Close
                </Button>
              </p>
            )}
          <Button variant="ghost" onClick={onManualFeedback}>
            Write feedback manually
          </Button>
        </div>
      )}
    </section>
  );
}
function CaptureReview({
  owner,
  flow,
  players,
  onClose,
  onPublished,
  observeApproval,
  effort,
}: {
  owner: DraftContext;
  flow: ReturnType<typeof useDraftInbox>;
  players: import("@/lib/types").Player[];
  onClose: () => void;
  onPublished: (message: string, request: ApprovalOperation) => void;
  observeApproval: (
    request: ApprovalOperation,
    work: () => Promise<unknown>,
  ) => Promise<unknown>;
  effort: AuthoringEffort;
}) {
  const item = flow.item!;
  const [selection, setSelection] = useState<CoachingPublicationSelection>({
    kind: "feedback",
    publication_class: "player",
    player_ids: [],
  });
  const [preview, setPreview] = useState<CoachingPublicationPreview | null>(
    null,
  );
  const [editingConfirmation, setEditingConfirmation] = useState(false);
  const currentConfirmation =
    item.source_revision &&
    !item.confirmation_code &&
    !flow.confirmation.error &&
    item.confirmation &&
    flow.confirmation.data?.id === item.confirmation.id &&
    flow.confirmation.data.revision === item.confirmation.revision
      ? flow.confirmation.data
      : null;
  const confirmation = editingConfirmation ? null : currentConfirmation;
  const [reviewed, setReviewed] = useState(false);
  const factForm = useForm<ConfirmationForm>({
    resolver: zodResolver(confirmationFactSchema),
    defaultValues: {
      statement: "",
      basis: "reported",
      relationship: "unknown",
      observerId: null,
      precision: "unknown",
      occurrence: "",
    },
  });
  const statement = factForm.watch("statement"),
    basis = factForm.watch("basis"),
    relationship = factForm.watch("relationship"),
    date = factForm.watch("occurrence"),
    precision = factForm.watch("precision"),
    observerId = factForm.watch("observerId");
  const setStatement = (value: string) =>
    factForm.setValue("statement", value, { shouldDirty: true });
  const setBasis = (value: ConfirmationForm["basis"]) =>
    factForm.setValue("basis", value, { shouldDirty: true });
  const setRelationship = (value: ConfirmationForm["relationship"]) =>
    factForm.setValue("relationship", value, { shouldDirty: true });
  const setObserverId = (value: string | null) =>
    factForm.setValue("observerId", value, { shouldDirty: true });
  const setPrecision = (value: ConfirmationForm["precision"]) =>
    factForm.setValue("precision", value, { shouldDirty: true });
  const setDate = (value: string) =>
    factForm.setValue("occurrence", value, { shouldDirty: true });
  const me = useAuthStore((s) => s.me);
  const selfRoles =
    me?.memberships
      .filter(
        (m) =>
          m.team_id === owner.teamId &&
          ["coach", "assistant_coach"].includes(m.role),
      )
      .map((m) => m.role) ?? [];
  const observers = [
    {
      value: "unknown",
      label: "Observer unknown",
      id: null,
      relationship: "unknown" as const,
    },
    ...selfRoles.map((role) => ({
      value: `${owner.actorId}:${role}`,
      label: `${me?.user.display_name || "Me"} · ${role.replace("_", " ")}`,
      id: owner.actorId,
      relationship: role as "coach" | "assistant_coach",
    })),
    ...players
      .filter((p) => selection.player_ids.includes(p.id))
      .flatMap((p) =>
        p.links
          .filter(
            (link) =>
              link.relationship === "guardian" || link.relationship === "self",
          )
          .map((link) => ({
            value: `${link.user_id}:${link.relationship}`,
            label: `${link.display_name || "Name unavailable"} · ${link.relationship}`,
            id: link.user_id,
            relationship: link.relationship as "guardian" | "self",
          })),
      ),
  ];
  const validObservers = observers.filter(
    (o, i, list) =>
      list.findIndex((x) => x.value === o.value) === i &&
      (basis === "reported" ||
        ["coach", "assistant_coach"].includes(o.relationship)),
  );
  const [admittedJob, setAdmittedJob] = useState<CoachingAIJobProgress | null>(
    null,
  );
  const currentJob = item.job ?? admittedJob;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [receipt, setReceipt] = useState<string | null>(null);
  const confirmationOp = useRef(
    new FrozenOperation<CreateCoachingConfirmationRequest>(),
  );
  const generationOp = useRef(
    new FrozenOperation<{
      id: string;
      body: GenerateConfirmedCoachingDraftRequest;
    }>(),
  );
  const approvalOp = useRef(
    new FrozenOperation<{
      captureId: string;
      draftId: string;
      body: ApproveConfirmedCoachingDraftRequest;
    }>(),
  );
  const discardOp = useRef(
    new FrozenOperation<DiscardCoachingCaptureRequest>(),
  );
  const [awaiting, setAwaiting] = useState<
    "confirmation" | "generate" | "approve" | "discard" | null
  >(null);
  const alive = useRef(true);
  const currentSource = useRef({
    id: item.capture_id,
    revision: item.source_revision,
  });
  currentSource.current = {
    id: item.capture_id,
    revision: item.source_revision,
  };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    setPreview(null);
    setReviewed(false);
    setAdmittedJob(null);
  }, [item.source_revision]);
  const attempt = async (
    kind: typeof awaiting,
    work: () => Promise<unknown>,
    settle?: (result: unknown) => void,
  ) => {
    if (inFlight.current || !isCurrentDraftContext(owner)) return;
    const dispatchedSource = { ...currentSource.current };
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await flow.mutation.mutateAsync(work);
      if (alive.current && isCurrentDraftContext(owner)) {
        if (
          currentSource.current.id !== dispatchedSource.id ||
          currentSource.current.revision !== dispatchedSource.revision
        ) {
          setError(
            "Source changed while this request was pending. Refresh the current review.",
          );
          setAwaiting(kind);
          setPreview(null);
          setReviewed(false);
          return;
        }
        setAwaiting(null);
        settle?.(result);
      }
    } catch (e) {
      if (alive.current && isCurrentDraftContext(owner)) {
        setError(errorMessage(e));
        setAwaiting(kind);
        setPreview(null);
        setReviewed(false);
        void flow.source.refetch();
        void flow.confirmation.refetch();
        void flow.draft.refetch();
      }
    } finally {
      inFlight.current = false;
      if (alive.current && isCurrentDraftContext(owner)) setBusy(false);
    }
  };
  const sourcePermitted =
    !!item.source_revision &&
    flow.source.data?.capture.revision === item.revision &&
    !!flow.source.data &&
    !flow.source.error &&
    !flow.inbox.error;
  useEffect(() => {
    if (
      !item.source_revision ||
      [
        flow.source.error,
        flow.inbox.error,
        flow.confirmation.error,
        flow.draft.error,
      ].some((e) => e instanceof ApiError && [401, 403, 404].includes(e.status))
    )
      clearReviewCarry(owner);
  }, [
    flow.source.error,
    flow.inbox.error,
    item.source_revision,
    flow.confirmation.error,
    flow.draft.error,
    owner,
  ]);
  const previewAudience = preview ?? confirmation;
  const audienceOK =
    !!previewAudience &&
    (!preview || preview.capture_source_revision === item.source_revision) &&
    previewAudience.selection.player_ids.every((id) =>
      players.some((p) => p.id === id),
    ) &&
    recipientsReviewable(
      previewAudience.recipients,
      previewAudience.recipient_user_ids,
    );
  useEffect(() => {
    setPreview(null);
    setReviewed(false);
  }, [currentConfirmation?.preview_revision]);
  const refreshAudience = () =>
    void attempt(
      null,
      () => flow.preview(item.capture_id, confirmation?.selection ?? selection),
      (result) => {
        setPreview(result as CoachingPublicationPreview);
        setReviewed(false);
      },
    );
  const confirm = () => {
    if (
      !preview ||
      !reviewed ||
      !audienceOK ||
      !statement.trim() ||
      !sourcePermitted
    )
      return;
    const request = confirmationOp.current.begin(() => ({
      client_operation_id: crypto.randomUUID(),
      expected_capture_revision: preview.capture_revision,
      expected_confirmation_revision: preview.confirmation_revision,
      preview_revision: preview.preview_revision,
      recipient_user_ids: preview.recipient_user_ids,
      selection: preview.selection,
      fact: humanConfirmationFact(factForm.getValues()),
    }));
    void attempt(
      "confirmation",
      () => flow.confirm(item.capture_id, request),
      () => {
        setEditingConfirmation(false);
        confirmationOp.current.reset();
      },
    );
  };
  const generate = () => {
    if (!confirmation || !sourcePermitted) return;
    const request = generationOp.current.begin(() => ({
      id: confirmation.id,
      body: {
        client_operation_id: crypto.randomUUID(),
        expected_confirmation_revision: confirmation.revision,
      },
    }));
    void attempt(
      "generate",
      () => flow.generate(request.id, request.body),
      (result) => {
        setAdmittedJob(result as CoachingAIJobProgress);
        generationOp.current.reset();
      },
    );
  };
  const [metricLabels, setMetricLabels] = useState<EditCategory[] | null>(null);
  const discard = () => {
    const request = discardOp.current.begin(() => ({
      client_operation_id: crypto.randomUUID(),
      expected_revision: item.revision,
      status: "discarded",
    }));
    void attempt(
      "discard",
      async () => {
        const measurement = effort.session;
        if (flow.draft.data?.draft.schema_version === "coaching-draft.v3") await measurement?.beforeSave({ id: flow.draft.data.draft.id, revision: flow.draft.data.draft.revision }).catch(() => undefined);
        if (!isCurrentDraftContext(owner)) throw new Error("Coaching context changed");
        let result: unknown;
        try { result = await flow.discard(item.capture_id, request); } catch (error) { measurement?.domainFailed(); throw error; }
        void measurement?.received({ event: "outcome", outcome: "discarded", domain_operation_id: request.client_operation_id });
        return result;
      },
      () => {
        clearReviewCarry(owner);
        onClose();
      },
    );
  };
  const draft =
    flow.draft.error || !sourcePermitted || !confirmation
      ? undefined
      : flow.draft.data?.draft;
  const published = (request: ApprovalOperation, result: unknown) => {
    const r = result as import("@/lib/types").CoachingApprovalResponse;
    clearReviewCarryForOrigin(owner, request);
    setReceipt(`Published ${r.artifact_kind} #${r.artifact_id}`);
    onPublished(`Published ${r.artifact_kind} #${r.artifact_id}`, request);
    approvalOp.current.reset();
  };
  const approve = (
    fields: CoachingApprovalFields,
    video?: CoachingVideoReference,
  ) => {
    if (
      !draft ||
      !confirmation ||
      !preview ||
      !reviewed ||
      !audienceOK ||
      !sourcePermitted
    )
      return;
    const request = approvalOp.current.begin(() => ({
      captureId: item.capture_id,
      draftId: draft.id,
      metricLabels: metricLabels === null ? null : [...metricLabels],
      body: {
        client_operation_id: crypto.randomUUID(),
        expected_draft_revision: draft.revision,
        confirmation_id: confirmation.id,
        expected_confirmation_revision: confirmation.revision,
        preview_revision: preview.preview_revision,
        recipient_user_ids: preview.recipient_user_ids,
        selection: preview.selection,
        fields,
        ...(video ? { video_reference: video } : {}),
      },
    }));
    void attempt(
      "approve",
      () =>
        observeApproval(request, () => flow.approve(request.draftId, request.body)),
      (result) => published(request, result),
    );
  };
  if (receipt)
    return (
      <p role="status">
        {receipt}
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </p>
    );
  return (
    <div
      className="grid min-w-0 gap-3 border-t border-rule pt-3"
      data-testid="draft-inbox-review"
    >
      <AuthoringEffortControls effort={effort} labels={metricLabels} onLabels={setMetricLabels} input={() => ({ task_kind: (confirmation?.selection ?? selection).kind === "action" ? "goal" : "feedback", mode: "assisted", player_ids: (confirmation?.selection ?? selection).player_ids, origin: { kind: "capture", source_id: item.capture_id, source_revision: item.revision } })} />
      <div className="flex min-w-0 flex-wrap gap-2">
        <h3 className="font-display">Review saved observation</h3>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button variant="ghost" disabled={busy || !!awaiting} onClick={discard}>
          Discard private capture
        </Button>
      </div>
      {flow.source.isLoading && <p role="status">Loading permitted source…</p>}
      {!sourcePermitted && (
        <p role="alert">
          Source unavailable. Review and publication are paused.
        </p>
      )}
      {sourcePermitted && (
        <section>
          <h4 className="text-sm font-display">Original private capture</h4>
          <p className="whitespace-pre-wrap text-sm">
            {flow.source.data!.capture.text ??
              "Silent marker: supply your own factual statement."}
          </p>
          <p className="text-xs text-ink-muted">
            Recorded {new Date(item.occurred_at).toLocaleString()} · Received{" "}
            {new Date(item.received_at).toLocaleString()} · Expires{" "}
            {new Date(item.expires_at).toLocaleString()}. Capture time does not
            establish fact occurrence. Player hints remain unresolved.
          </p>
        </section>
      )}
      {error && <p role="alert">{error}</p>}
      {awaiting && (
        <div className="flex flex-wrap gap-2">
          <p className="text-sm">
            Acknowledgment is unresolved. Retry the exact frozen operation.
          </p>
          <Button
            disabled={busy}
            onClick={() => {
              const op = awaiting;
              if (op === "confirmation") {
                const r = confirmationOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                void attempt(
                  op,
                  () => flow.confirm(item.capture_id, r),
                  () => {
                    setEditingConfirmation(false);
                    confirmationOp.current.reset();
                  },
                );
              } else if (op === "generate") {
                const r = generationOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                void attempt(
                  op,
                  () => flow.generate(r.id, r.body),
                  (result) => {
                    setAdmittedJob(result as CoachingAIJobProgress);
                    generationOp.current.reset();
                  },
                );
              } else if (op === "discard") discard();
              else {
                const r = approvalOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                const request: ApprovalOperation = r;
                void attempt(
                  op,
                  () => observeApproval(request, () => flow.approve(request.draftId, request.body)),
                  (result) => published(request, result),
                );
              }
            }}
          >
            Retry unchanged operation
          </Button>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => {
              confirmationOp.current.reset();
              generationOp.current.reset();
              approvalOp.current.reset();
              discardOp.current.reset();
              setAwaiting(null);
              setReviewed(false);
            }}
          >
            Begin a new review operation
          </Button>
        </div>
      )}
      {!confirmation && sourcePermitted && (
        <fieldset disabled={busy || !!awaiting} className="grid gap-3">
          <TkticSelect
            ariaLabel="Publication artifact"
            disabled={busy || !!awaiting}
            value={selection.kind}
            onChange={(kind) => {
              setSelection({
                kind: kind as "feedback" | "action",
                publication_class: "player",
                player_ids: [],
              });
              setPreview(null);
              setReviewed(false);
            }}
            options={[
              { value: "feedback", label: "Feedback" },
              { value: "action", label: "Single-player action" },
            ]}
          />
          <p className="text-sm">
            Resolve the real player{selection.kind === "feedback" ? "s" : ""}{" "}
            explicitly.
          </p>
          <ComposerPlayerPicker
            players={players}
            value={selection.player_ids}
            onChange={(player_ids) => {
              setSelection({
                ...selection,
                player_ids:
                  selection.kind === "action"
                    ? player_ids.slice(-1)
                    : player_ids,
              });
              setPreview(null);
              setReviewed(false);
            }}
          />
          <TkticSelect
            ariaLabel="Publication audience"
            disabled={busy || !!awaiting}
            value={selection.publication_class}
            onChange={(publication_class) => {
              setSelection({
                ...selection,
                publication_class:
                  publication_class as CoachingPublicationSelection["publication_class"],
              });
              setPreview(null);
              setReviewed(false);
            }}
            options={[
              { value: "creator_private", label: "Only me" },
              {
                value: "player",
                label: "Selected players and their linked family",
              },
              ...(selection.kind === "feedback"
                ? [{ value: "team", label: "Team audience" }]
                : []),
            ]}
          />
          <label className="text-sm">
            Human factual statement (separate from proposed wording)
            <textarea
              value={statement}
              maxLength={990}
              onChange={(e) => setStatement(e.target.value)}
              className="mt-1 w-full rounded-control border border-rule bg-canvas p-2"
            />
          </label>
          <TkticSelect
            ariaLabel="Factual basis"
            disabled={busy || !!awaiting}
            value={basis}
            onChange={(v) => {
              setBasis(v as typeof basis);
              setRelationship("unknown");
              setObserverId(null);
            }}
            options={[
              { value: "reported", label: "Reported to me" },
              { value: "observed", label: "Personally observed" },
            ]}
          />
          <TkticSelect
            ariaLabel="Actual observer"
            disabled={busy || !!awaiting}
            value={
              relationship === "unknown"
                ? "unknown"
                : `${observerId}:${relationship}`
            }
            onChange={(value) => {
              const selected = validObservers.find((o) => o.value === value);
              if (selected) {
                setObserverId(selected.id);
                setRelationship(selected.relationship);
              }
            }}
            options={validObservers.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            placeholder="Select the actual observer"
          />
          <TkticSelect
            ariaLabel="Occurrence precision"
            disabled={busy || !!awaiting}
            value={precision}
            onChange={(v) => {
              setPrecision(v as typeof precision);
              setDate("");
            }}
            options={[
              { value: "unknown", label: "Occurrence unknown" },
              { value: "day", label: "Known day" },
              { value: "instant", label: "Known instant with timezone" },
            ]}
          />
          {precision === "day" ? (
            <label className="text-sm">
              Actual occurrence day
              <TkticDateInput value={date} onChange={setDate} />
            </label>
          ) : precision === "instant" ? (
            <label className="text-sm">
              Actual occurrence instant with timezone
              <input
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="2026-10-07T15:00:00Z"
                className="mt-1 w-full rounded-control border border-rule bg-canvas p-2"
              />
            </label>
          ) : (
            <p className="text-sm text-ink-muted">
              Occurrence stays unknown; capture time is not substituted.
            </p>
          )}
        </fieldset>
      )}
      {confirmation && sourcePermitted && (
        <section>
          <h4 className="font-display">Confirmed human fact</h4>
          <p className="whitespace-pre-wrap text-sm">
            {confirmation.fact.statement}
          </p>
          <p className="text-xs text-ink-muted">
            {confirmation.fact.basis} ·{" "}
            {confirmation.fact.observer_relationship} ·{" "}
            {confirmation.fact.occurrence_precision === "unknown"
              ? "Occurrence unknown"
              : confirmation.fact.occurred_at}{" "}
            · Reviewed by you. Final wording does not change this fact.
          </p>
        </section>
      )}
      {confirmation && (
        <Button
          variant="outline"
          disabled={busy || !!awaiting}
          onClick={() => {
            setSelection(confirmation.selection);
            setStatement(confirmation.fact.statement);
            setBasis(confirmation.fact.basis);
            setRelationship(
              confirmation.fact.observer_user_id === owner.actorId
                ? confirmation.fact.observer_relationship
                : "unknown",
            );
            setObserverId(confirmation.fact.observer_user_id ?? null);
            setRelationship(confirmation.fact.observer_relationship);
            setPrecision(confirmation.fact.occurrence_precision);
            setDate(confirmation.fact.occurred_at ?? "");
            setEditingConfirmation(true);
            setPreview(null);
            setReviewed(false);
          }}
        >
          Change confirmed evidence or selection
        </Button>
      )}
      <Button
        variant="outline"
        disabled={
          busy ||
          !!awaiting ||
          !sourcePermitted ||
          (!confirmation && !selection.player_ids.length)
        }
        onClick={refreshAudience}
      >
        Refresh complete recipient preview
      </Button>
      {previewAudience && sourcePermitted && (
        <section>
          <h4 className="font-display">Complete publication audience</h4>
          <p className="text-sm">
            {previewAudience.selection.publication_class} ·{" "}
            {previewAudience.selection.player_ids
              .map(
                (id) =>
                  players.find((p) => p.id === id)?.display_name ??
                  "Player name unavailable",
              )
              .join(", ")}
          </p>
          {previewAudience.recipients.length ? (
            <ul>
              {previewAudience.recipients.map((r) => (
                <li key={r.user_id} className="text-sm">
                  {r.label_status === "available" && r.display_name
                    ? r.display_name
                    : "Recipient name unavailable — review incomplete"}{" "}
                  · {r.relationships.join(", ")}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm">Only you. No player or family recipients.</p>
          )}
          <Button
            variant={reviewed ? "primary" : "outline"}
            disabled={!preview || !audienceOK || busy || !!awaiting}
            onClick={() => setReviewed((v) => !v)}
            aria-pressed={reviewed}
          >
            {reviewed
              ? "Audience reviewed"
              : "I reviewed every actual recipient and resolved player"}
          </Button>
        </section>
      )}
      {!confirmation && (
        <Button
          disabled={
            !reviewed ||
            !preview ||
            !statement.trim() ||
            busy ||
            !!awaiting ||
            !sourcePermitted ||
            !confirmationFactSchema.safeParse(factForm.getValues()).success ||
            (relationship !== "unknown" &&
              !validObservers.some(
                (o) => o.id === observerId && o.relationship === relationship,
              ))
          }
          onClick={confirm}
        >
          Confirm factual evidence and audience
        </Button>
      )}
      {confirmation && !draft && (
        <>
          <Button
            disabled={
              busy ||
              !!awaiting ||
              !sourcePermitted ||
              (!!currentJob &&
                (!currentJob.can_regenerate ||
                  ["queued", "processing"].includes(currentJob.status)))
            }
            onClick={generate}
          >
            Generate from confirmed evidence
          </Button>
          {currentJob && (
            <p role="status" className="text-sm">
              {currentJob.status} · {currentJob.code}
            </p>
          )}
          <p className="text-sm text-ink-muted">
            External processing remains denied unless separately authorized. If
            generation is blocked, write normal feedback manually.
          </p>
        </>
      )}
      {(flow.confirmation.error ||
        flow.draft.error ||
        item.confirmation_code ||
        item.draft_code) && (
        <p role="alert">
          Current confirmation or proposal is stale or unavailable. Refresh
          current source and audience before continuing.
        </p>
      )}
      {draft && draft.schema_version !== "coaching-draft.v3" && <p role="status">This proposal uses an older evidence version. Regenerate from current confirmation before review or approval.</p>}
      {draft &&
        draft.schema_version === "coaching-draft.v3" &&
        draft.status === "needs_review" &&
        confirmation && (
          <ApprovalWriting
            key={`${draft.id}:${draft.revision}:${confirmation.revision}`}
            owner={owner}
            players={players}
            draft={draft}
            confirmation={confirmation}
            disabled={
              busy || !!awaiting || !preview || !reviewed || !audienceOK
            }
            onApprove={approve}
          />
        )}
    </div>
  );
}
const goalSchema = z.object({
  title: z.string().trim().min(1).max(160),
  description: z.string().max(2000),
  successCriteria: z.string().max(2000),
});
function ApprovalWriting({
  owner,
  players,
  draft,
  confirmation,
  disabled,
  onApprove,
}: {
  owner: DraftContext;
  players: import("@/lib/types").Player[];
  draft: CoachingApplicationDraft;
  confirmation: CoachingConfirmationResponse;
  disabled: boolean;
  onApprove: (
    fields: CoachingApprovalFields,
    video?: CoachingVideoReference,
  ) => void;
}) {
  const navigate = useNavigate();
  const composer = useRef<ComposerHandle>(null);
  const stored = useRef(readReviewCarry(owner));
  const origin = {
    captureId: draft.capture_id,
    draftId: draft.id,
    draftRevision: draft.revision,
    confirmationId: confirmation.id,
    confirmationRevision: confirmation.revision,
  };
  const same =
    stored.current?.kind === draft.kind &&
    JSON.stringify(stored.current?.origin) === JSON.stringify(origin);
  const recoverable =
    stored.current?.kind === draft.kind &&
    stored.current?.origin.captureId === origin.captureId;
  const defaults = {
    ...proposedWriting(draft),
    ...(same ? stored.current?.edits : {}),
  };
  const [video, setVideo] = useState(same ? stored.current?.video : undefined);
  const retainedEdits = useRef(same ? stored.current?.edits : {});
  const goal = useForm<z.infer<typeof goalSchema>>({
    resolver: zodResolver(goalSchema),
    defaultValues: {
      title: defaults.title,
      description: defaults.description ?? "",
      successCriteria: defaults.success_criteria ?? "",
    },
  });
  const persist = () => {
    const values = goal.getValues();
    const dirty = goal.formState.dirtyFields;
    saveReviewCarry(owner, {
      kind: draft.kind,
      origin,
      edits: boundedHumanEdits(
        {
          ...(retainedEdits.current ?? {}),
          ...(draft.kind === "feedback"
            ? (composer.current?.getDirtyValues() ?? {})
            : {
                ...(dirty.title ? { title: values.title } : {}),
                ...(dirty.description
                  ? { description: values.description }
                  : {}),
                ...(dirty.successCriteria
                  ? { success_criteria: values.successCriteria }
                  : {}),
              }),
        },
        draft.kind,
      ),
      ...(video ? { video } : {}),
    });
  };
  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => {
    const onPageHide = () => persistRef.current();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);
  return (
    <section
      onChangeCapture={() => queueMicrotask(() => persistRef.current())}
      className="grid gap-3 border-t border-rule pt-3"
    >
      <h4 className="font-display">
        Proposed {draft.kind === "feedback" ? "feedback" : "action"} — edit
        before approval
      </h4>
      <p className="text-xs text-ink-muted">
        Proposal received {new Date(draft.received_at).toLocaleString()};
        expires {new Date(draft.expires_at).toLocaleString()}. Source
        references:{" "}
        {draft.source_references
          .map((r) => `${r.kind} revision ${r.source_revision}`)
          .join(", ")}
      </p>
      {draft.schema_version === "coaching-draft.v3" && (
        <ProposalEvidence output={draft.structured_output} players={players} />
      )}
      {!same && recoverable && (
        <div>
          <p className="text-sm">
            A newer server revision is available. Your separately typed edits
            are retained; recording proof must be selected again.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              const edits = stored.current?.edits ?? {};
              retainedEdits.current = edits;
              if (draft.kind === "feedback")
                composer.current?.reset({
                  ...proposedWriting(draft),
                  ...edits,
                });
              else {
                goal.setValue("title", edits.title ?? defaults.title, {
                  shouldDirty: true,
                });
                goal.setValue(
                  "description",
                  edits.description ?? defaults.description ?? "",
                  { shouldDirty: true },
                );
                goal.setValue(
                  "successCriteria",
                  edits.success_criteria ?? defaults.success_criteria ?? "",
                  { shouldDirty: true },
                );
              }
              saveReviewCarry(owner, { kind: draft.kind, origin, edits });
            }}
          >
            Apply my retained human edits to this revision
          </Button>
        </div>
      )}
      {draft.kind === "feedback" ? (
        <>
          <Composer
            ref={composer}
            approvalMode
            players={[]}
            defaultValues={defaults}
            disabled={disabled}
            submitLabel="Approve and publish once"
            onSubmit={(values) =>
              onApprove(
                {
                  ...proposedWriting(draft),
                  ...boundedHumanEdits(values, "feedback"),
                },
                video,
              )
            }
          />
          {video && (
            <p className="text-sm">
              Human-selected source recording: {video.slot} at{" "}
              {video.timestamp_seconds.toFixed(1)} seconds. This does not
              establish fact occurrence.{" "}
              <Button
                variant="ghost"
                onClick={() => {
                  setVideo(undefined);
                  saveReviewCarry(owner, {
                    kind: draft.kind,
                    origin,
                    edits: boundedHumanEdits(
                      {
                        ...retainedEdits.current,
                        ...composer.current?.getDirtyValues(),
                      },
                      draft.kind,
                    ),
                  });
                }}
              >
                Remove recording reference
              </Button>
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => {
              persist();
              void navigate({
                to: "/coach/review",
                search: { source: "video", inbox_capture_id: origin.captureId },
              });
            }}
          >
            Add video evidence in Review
          </Button>
        </>
      ) : (
        <form
          onSubmit={goal.handleSubmit((values) =>
            onApprove({
              ...proposedWriting(draft),
              title: values.title,
              description: values.description,
              success_criteria: values.successCriteria,
            }),
          )}
          className="grid gap-3"
        >
          <GoalWritingFields
            title={goal.register("title")}
            description={goal.register("description")}
            successCriteria={goal.register("successCriteria")}
            errors={goal.formState.errors}
          />
          <p className="text-sm text-ink-muted">
            Actions cannot attach video. One normal fact note and one player
            goal will be created.
          </p>
          <Button disabled={disabled} type="submit">
            Approve and publish once
          </Button>
        </form>
      )}
    </section>
  );
}
